import { UI_PRIMITIVES } from '@/core/constants/uiColors';
import { DT_SCREEN } from '@/core/constants/dynatraceScreens';
import { useUIColors } from '@/core/hooks';
import { trackCatalogEvent } from '@/core/services/trackDynatraceCatalog';
import PaymentDetailsScreen, { PaymentDetailsScreenProps } from '@/modules/payments/PaymentDetailsScreen';
import { BankTransferDetailsDialog } from '@/modules/properties-web/components/BankTransferDetailsDialog';
import type { BankTransferAccount } from '@/modules/properties-web/constants/bankTransferDetails';
import React, { useEffect, useRef, useState } from 'react';
import { Modal, Pressable, StyleSheet } from 'react-native';

export interface PaymentDetailsDialogProps {
  visible: boolean;
  // Allow callers to pass string | undefined; we will only forward when defined
  paymentId: string | undefined;
  onClose: () => void;
}

/**
 * Wrapper that shows PaymentDetailsScreen inside a centered dialog/modal.
 * Used on web to avoid changing the route when viewing payment details.
 */
export const PaymentDetailsDialog: React.FC<PaymentDetailsDialogProps> = ({
  visible,
  paymentId,
  onClose,
}) => {
  const colors = useUIColors();
  const viewedRef = useRef(false);
  const [bankAccount, setBankAccount] = useState<BankTransferAccount | null | undefined>(
    undefined,
  );
  const bankVisible = bankAccount !== undefined;

  useEffect(() => {
    if (visible && paymentId && !viewedRef.current) {
      viewedRef.current = true;
      trackCatalogEvent({
        screen: DT_SCREEN.PAYMENT_DETAILS,
        actionName: 'payment_details_dialog_open',
        eventType: 'view',
        properties: { paymentId },
      });
    }
    if (!visible) {
      viewedRef.current = false;
      setBankAccount(undefined);
    }
  }, [visible, paymentId]);

  const handleViewBankDetails = (account: BankTransferAccount | null) => {
    setBankAccount(account);
  };

  const handleBankClose = () => {
    onClose();
  };

  const screenProps = {
    ...(paymentId ? { paymentId } : {}),
    onBackPress: onClose,
    onPayNowSideEffect: onClose,
    onViewBankDetails: handleViewBankDetails,
    useCloseIcon: true, // Use X icon for dialog close button
  } as PaymentDetailsScreenProps;

  return (
    <>
      <Modal
        visible={visible && !bankVisible}
        transparent
        animationType="fade"
        onRequestClose={onClose}
      >
        <Pressable style={styles.overlay} onPress={onClose}>
          <Pressable
            style={[styles.container, { backgroundColor: colors.backgroundLevel1 }]}
            onPress={(e) => e.stopPropagation()}
          >
            <PaymentDetailsScreen {...screenProps} />
          </Pressable>
        </Pressable>
      </Modal>
      <BankTransferDetailsDialog
        visible={visible && bankVisible}
        viewOnly
        account={bankAccount ?? null}
        onBack={handleBankClose}
        onClose={handleBankClose}
        onConfirmTransfer={handleBankClose}
      />
    </>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: UI_PRIMITIVES.overlayBackdrop,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 24,
  },
  container: {
    borderRadius: 24,
    paddingTop: 24,
    paddingBottom: 24,
    paddingHorizontal: 24,
    width: '100%',
    maxWidth: 600,
    maxHeight: '90%',
    overflow: 'hidden',
  },
});

export default PaymentDetailsDialog;


